import { useEffect } from "react";
import type { Field, FieldRefs, FieldValues, Path, UseFormReturn } from "react-hook-form";

/**
 * Drží `<select>`y v souladu s hodnotami formuláře, i když jejich `<option>`y dorazí později.
 *
 * Proč to musí existovat: edit stránky načtou záznam jedním dotazem a číselníky pro rozbalovací
 * seznamy (uživatelé, destinace, dodavatelé…) dalšími, samostatnými dotazy. `reset()` proběhne,
 * jakmile dorazí záznam — v tu chvíli `<select>` ještě nemá `<option>` s uloženým id, takže
 * prohlížeč zápis `select.value = id` zahodí a spadne na první položku („— Nepřiřazeno —").
 * React-hook-form si správnou hodnotu ve stavu formuláře drží, ale operátor v UI vidí něco
 * jiného, než co je v databázi — a jakoukoli další úpravu dělá z falešného výchozího stavu.
 *
 * Řešení je v hooku, ne na jednotlivých stránkách: `useEditPage` je JEDINÉ místo v adminu, kde se
 * `useForm()` volá, takže tudy je pokrytá každá edit stránka včetně budoucích — žádné „nezapomeň
 * na stránce doplnit resetDeps".
 *
 * Efekt schválně nemá pole závislostí: běží po každém renderu, protože právě render s novými
 * `<option>`y je ten okamžik, kdy je potřeba hodnotu doplnit. Zapisuje jen při skutečném rozjetí
 * DOMu a stavu, takže uživateli nikdy nepřepíše jeho vlastní volbu (ta projde přes `onChange`
 * dřív, takže DOM i stav už souhlasí) a nikdy nevyvolá další render.
 */
export function useSelectValueSync<TForm extends FieldValues>(form: UseFormReturn<TForm>): void {
  const { control, getValues } = form;
  useEffect(() => {
    syncSelects(control._fields, (path) => getValues(path as Path<TForm>));
  });
}

/** Projde strom registrovaných polí (vnořená pole mají cestu `a.0.b`) a opraví `<select>`y. */
function syncSelects(fields: FieldRefs, getValue: (path: string) => unknown, prefix = ""): void {
  for (const [key, node] of Object.entries(fields)) {
    if (!node) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    const field = node as Field;
    if (field._f) {
      // Listové pole. `Controller` (MediaPicker, editory) tu má vlastní objekt místo elementu.
      if (field._f.ref instanceof HTMLSelectElement) applySelectValue(field._f.ref, getValue(path));
      continue;
    }
    syncSelects(node as FieldRefs, getValue, path);
  }
}

function applySelectValue(el: HTMLSelectElement, value: unknown): void {
  // Vícenásobný výběr admin nikde nepoužívá; hodnotou je pole a synchronizace by musela jít
  // po jednotlivých `<option>`. Až takový select vznikne, doplní se sem.
  if (el.multiple || value === undefined) return;
  // `valueAsNumber` pole drží číslo, DOM vždy řetězec.
  const expected = value === null ? "" : String(value);
  if (el.value === expected) return;
  // Option pořád chybí (číselník ještě nedojel, nebo hodnota ukazuje na smazaný záznam) —
  // zápis by jen zhasl výběr úplně. Stav formuláře zůstává zdrojem pravdy pro uložení.
  if (!Array.from(el.options).some((o) => o.value === expected)) return;
  el.value = expected;
}
